/*
  Build a Six.

  WHY THE ELEMENT REFERENCES ARE LOOKED UP LIVE AND NOT CACHED
  ------------------------------------------------------------
  The previous version cached this.count / this.total / this.status / this.submit /
  this.summary in connectedCallback. The section loads this script with a plain,
  parser-blocking <script> tag that sits ABOVE the element in the body, so
  customElements.define() had already run by the time the parser reached
  <siw-build-a-six>. A custom element upgrades the moment its START TAG is parsed —
  before any of its children exist — so every one of those querySelector calls
  returned null and stayed null for the life of the page.

  The visible symptom was subtle enough to survive launch: tapping + updated the
  per-card counter (that lookup happened live, inside render), but "0 of 6" never
  moved, the running total stayed at $0.00, the status line never changed, and
  "Add six to cart" was disabled forever. The page looked alive and could not sell
  a single six, on any device.

  Two independent guards now, because one of them silently failing is what caused
  this in the first place:
    1. the section tag carries "defer", so children exist before we run at all;
    2. nothing is cached — every lookup happens at render time against the live DOM,
       and the click handler is delegated on the host, which needs no children.
  Either one alone is sufficient. Both together mean a future change to how the
  script is loaded cannot resurrect this bug.
*/
class SIWBuildASix extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.limit = Number(this.dataset.limit) || 6;
    this.currency = this.dataset.currency || 'AUD';
    this.selected = new Map();

    // Delegated on the host: works whether or not the children have been parsed.
    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-six-toggle]');
      if (toggle) return this.toggleReview();
      const submit = event.target.closest('[data-six-submit]');
      if (submit) return this.addToCart();
      const add = event.target.closest('[data-six-add]');
      if (add) return this.increment(add);
      const remove = event.target.closest('[data-six-remove]');
      if (remove) return this.decrement(remove);
    });

    this.render();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.render(), { once: true });
    }
  }

  get bottleCount() {
    let n = 0;
    this.selected.forEach((item) => { n += item.quantity; });
    return n;
  }

  /*
    The running total has to read like every other price on the page. Intl formats
    AUD as "A$153.00" for anyone whose browser is not set to Australia, so the card
    said "$49.00" and the total beside it said "A$153.00". Shopify's own money_format
    is passed in from Liquid and applied here, which is what {{ | money }} does, so
    the two agree for every visitor regardless of their locale.
  */
  money(cents) {
    const format = this.dataset.moneyFormat || '';
    const value = cents / 100;
    const group = (decimals, thousands, decimal) => {
      const fixed = Math.abs(value).toFixed(decimals);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const sign = value < 0 ? '-' : '';
      return sign + (parts[1] ? whole + decimal + parts[1] : whole);
    };
    if (format) {
      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
        switch (token) {
          case 'amount': return group(2, ',', '.');
          case 'amount_no_decimals': return group(0, ',', '.');
          case 'amount_with_comma_separator': return group(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
          case 'amount_with_apostrophe_separator': return group(2, "'", '.');
          case 'amount_no_decimals_with_space_separator': return group(0, ' ', '.');
          case 'amount_with_space_separator': return group(2, ' ', ',');
          case 'amount_with_period_and_space_separator': return group(2, ' ', '.');
          default: return match;
        }
      });
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  toggleReview() {
    const open = this.hasAttribute('data-six-open');
    if (open) this.removeAttribute('data-six-open');
    else this.setAttribute('data-six-open', '');
    this.querySelectorAll('[data-six-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(!open)));
  }

  increment(button) {
    if (this.bottleCount >= this.limit) return;
    const id = String(button.dataset.variantId || '');
    if (!id) return;
    const current = this.selected.get(id) || {
      id: Number(id),
      title: button.dataset.title || '',
      price: Number(button.dataset.price || 0),
      quantity: 0,
    };
    current.quantity += 1;
    this.selected.set(id, current);
    this.render();
  }

  decrement(button) {
    const id = String(button.dataset.variantId || '');
    const current = this.selected.get(id);
    if (!current) return;
    current.quantity -= 1;
    if (current.quantity <= 0) this.selected.delete(id);
    else this.selected.set(id, current);
    this.render();
  }

  render() {
    const size = this.bottleCount;
    const remaining = this.limit - size;
    const full = size >= this.limit;

    this.querySelectorAll('[data-six-count]').forEach((el) => {
      el.textContent = size + ' of ' + this.limit;
    });

    this.querySelectorAll('[data-six-card]').forEach((card) => {
      const id = String(card.dataset.variantId || '');
      const quantity = (this.selected.get(id) || {}).quantity || 0;
      const output = card.querySelector('[data-six-quantity]');
      const add = card.querySelector('[data-six-add]');
      const remove = card.querySelector('[data-six-remove]');
      if (output) output.textContent = String(quantity);
      if (remove) remove.disabled = quantity === 0;
      if (add) add.disabled = full;
      card.toggleAttribute('data-six-chosen', quantity > 0);
    });

    const list = this.querySelector('[data-six-summary]');
    if (list) {
      list.textContent = '';
      if (!this.selected.size) {
        const li = document.createElement('li');
        li.className = 'siw-six__empty';
        li.textContent = 'Nothing picked yet.';
        list.appendChild(li);
      } else {
        this.selected.forEach((item) => {
          const li = document.createElement('li');

          const name = document.createElement('span');
          name.className = 'siw-six__line-name';
          name.textContent = item.quantity > 1 ? item.title + ' × ' + item.quantity : item.title;

          const price = document.createElement('span');
          price.className = 'siw-six__line-price';
          price.textContent = this.money(item.price * item.quantity);

          const drop = document.createElement('button');
          drop.type = 'button';
          drop.className = 'siw-six__line-remove';
          drop.setAttribute('data-six-remove', '');
          drop.dataset.variantId = String(item.id);
          drop.setAttribute('aria-label', 'Remove one ' + item.title);
          drop.textContent = '−';

          li.append(name, price, drop);
          list.appendChild(li);
        });
      }
    }

    let cents = 0;
    this.selected.forEach((item) => { cents += item.price * item.quantity; });
    this.querySelectorAll('[data-six-total]').forEach((el) => { el.textContent = this.money(cents); });

    this.querySelectorAll('[data-six-submit]').forEach((el) => { el.disabled = !full; });
    this.querySelectorAll('[data-six-status]').forEach((el) => {
      el.textContent = full
        ? 'Your six is ready.'
        : remaining + ' more bottle' + (remaining === 1 ? '' : 's') + ' needed.';
    });
  }

  setStatus(text) {
    this.querySelectorAll('[data-six-status]').forEach((el) => { el.textContent = text; });
  }

  async addToCart() {
    if (this.bottleCount !== this.limit) return;
    const buttons = [...this.querySelectorAll('[data-six-submit]')];
    buttons.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
    this.setStatus('Adding your six…');
    try {
      const items = [];
      this.selected.forEach(({ id, quantity }) => {
        items.push({ id, quantity, properties: { '_SIW build': 'Build a Six' } });
      });
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Cart add failed');
      window.location.assign(root + 'cart');
    } catch (error) {
      buttons.forEach((b) => { b.disabled = false; b.removeAttribute('aria-busy'); });
      this.setStatus("Couldn't add those bottles. Your selections are still here — try again.");
    }
  }
}
if (!customElements.get('siw-build-a-six')) customElements.define('siw-build-a-six', SIWBuildASix);
